<script lang="ts">
	//Dispatchers
	import { createEventDispatcher } from 'svelte';
	//Formatters
	import { date, format } from 'svelte-i18n';
	//Design system components
	import { Button, ButtonGroup, Stack, Select } from '@Shelly/shelly-design-system';
	import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
	//Helpres
	import { time_translate } from '../../helpers/utilities';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import {
		modeTypes,
		getMinOptions,
		getHoursOptions,
	} from '../DevicesSettings/Schedules/SchedulesHelpers/schedules';
	import TimePickerFormatWrapper from './TimePickerFormatWrapper.svelte';
	import { user_settings } from 'src/stores/account/user';

	// import TransitionWrapper from '../Utility/TransitionWrapper.svelte';

	export let time = '12:43',
		hoursRange = [0, 23],
		mode = modeTypes.sunrise;

	const dispatch = createEventDispatcher();

	$: hoursOptions = getHoursOptions(hoursRange[0], hoursRange[1]);

	const minsOptions = getMinOptions();

	let hours = clean_hours(time_translate(time)[0]),
		mins = Number(time_translate(time)[1]),
		formatedHours = time_translate(time)[0].replace(/\-/gi, ''),
		formatedMins: number | string = time_translate(time)[1], //number string because of need to sent 00 as mins
		negatives = hoursRange[1] === 12, //this will be active only on Sunrise/Sunset
		options = [$format('text_before'), $format('text_after')], //the plus minus replacements
		sign =
			typeof time[0] !== 'object'
				? time[0].toString().includes('-')
					? options[0]
					: options[1]
				: ''; //this is general sign, will be overridden if conditions are met

	function zero_values() {
		//if type is changed from Time to Sunrise/Sunset or from Sunrise/Sunset to Time will zero values
		hours = 0;
		mins = 0;
		formatedHours = '00';
		formatedMins = '00';
	}

	function clean_hours(val) {
		//this will be used to be sure that there are no signs infront of hours
		return Number(val.toString().replace(/\-/gi, '').replace(/\+/gi, ''));
	}
	const sent_data = () => {
		// here we send hours (eu time) and we add 0 on the front
		// when we have less than 10 so the formatting work and we get 02:00 instead of 20:0
		dispatch(
			'change',
			`${sign === options[0] ? '-' : ''}${hours.toString().padStart(2, '0')}:${formatedMins}`,
		); //this is for the sake of reactivity. It will dispatch change at any manipulation
	};
	$: if (hoursRange.some((x) => x === 12)) {
		//this will look if time was changed. Negative values will appear only on Sunrise/Sunset conditions else values will be positive. From now will be before/after radio
		if (!negatives) {
			zero_values();
			sent_data();
		}
		negatives = true; //activated by Sunrise/Sunset
	} else {
		if (negatives) {
			sign = options[1];
			mode = modeTypes.sunrise;
			zero_values();
			sent_data();
		}
		negatives = false; //deactivated by Time
	}

	/**
	 * this function is triggered only by mins time picker
	 * @param e .detail this is the event itself which carry all object from picker:
	 * {
	 * 	formatedHours: "12",
	 * 	formatedMins: "58",
	 * 	hours: 12,
	 * 	mins: 58,
	 * 	time: "12:58"
	 * }
	 */
	const time_change = (e) => {
		//Well here we get data from UXPs time picker and pread it trough variables
		formatedHours = e.detail.formatedHours;
		formatedMins = e.detail.formatedMins;
		hours = e.detail.hours;
		mins = e.detail.mins;
		time = e.detail.time;
		if (negatives && hours === 12) {
			//While on sunrise/sunset deny more than 12 hrs offset
			mins = 0;
			formatedMins = '00';
		}
		sent_data();
	};

	const handle_sign_change = (option) => {
		//play with before and after
		sign = option;
		sent_data();
	};

	const createTimeData = (e: CustomEvent, type: 'hour' | 'min' = 'hour') => {
		const selectedValue: SelectOptionPropsType = e.detail;
		const currentHours = type === 'hour' ? selectedValue.value : hours;
		const currentMins = type === 'min' ? selectedValue.value : mins;
		const currentFormatedHours = type === 'hour' ? selectedValue.title.split(' ')[0] : formatedHours;
		const currentFormatedMins = type === 'min' ? selectedValue.title.split(' ')[0] : formatedMins;

		return {
			hours: currentHours,
			formatedHours: currentFormatedHours,
			mins: currentMins,
			formatedMins: currentFormatedMins,
			time: `${currentFormatedHours}:${currentFormatedMins}`,
		};
	};
</script>

<slot />

{#if negatives}
	<div class="d-flex mb-m justify-content-center">
		<Select
			aria-label={$format('select_hours', { default: 'Select hours' })}
			class="width-md-s width-sm-full mr-s"
			icon="fas fa-clock"
			options={hoursOptions}
			value={hoursOptions.find((o) => o.value === hours)}
			on:change={(e) => {
				const timeData = createTimeData(e);
				time_change({ ...e, detail: timeData });
			}}
			id="select-3f24kd5"
		/>
		<Select
			aria-label={$format('select_minutes', { default: 'Select minutes' })}
			class="width-md-s width-sm-full"
			icon="fas fa-clock"
			disabled={negatives && hours == 12}
			options={minsOptions}
			value={minsOptions.find((o) => o.value === mins)}
			on:change={(e) => {
				const timeData = createTimeData(e, 'min');
				time_change({ ...e, detail: timeData });
			}}
			id="select-0jgdf9c"
		/>
	</div>
	<!-- <TransitionWrapper> -->
	<div class="d-flex justify-content-center">
		<Stack gap="s" direction="column">
			<ButtonGroup
				id="buttongroup-g75kqf8"
				aria-label={$format('label_before_after', { default: 'Before After' })}
			>
				<Button
					class={classMap({
						active: sign === options[0],
						'text-capitalize': true,
					})}
					size="m"
					on:click={() => handle_sign_change(options[0])}>{options[0]}</Button
				>
				<Button
					class={classMap({
						active: sign === options[1],
						'text-capitalize': true,
					})}
					size="m"
					on:click={() => handle_sign_change(options[1])}>{options[1]}</Button
				>
			</ButtonGroup>

			<ButtonGroup
				id="buttongroup-495j7c3"
				aria-label={$format('label_sunrise_sunset', { default: 'Sunrise Sunset' })}
			>
				<Button
					class={classMap({ active: mode === modeTypes.sunrise })}
					size="m"
					on:click={() => {
						mode = modeTypes.sunrise;
						dispatch('change-mode', mode);
					}}>{$format('schedule_opt_sunrise')}</Button
				>
				<Button
					class={classMap({ active: mode === modeTypes.sunset })}
					size="m"
					on:click={() => {
						mode = modeTypes.sunset;
						dispatch('change-mode', mode);
					}}>{$format('schedule_opt_sunset')}</Button
				>
			</ButtonGroup>
		</Stack>
	</div>
	<!-- </TransitionWrapper> -->
{:else}
	<TimePickerFormatWrapper bind:hours {mins} on:change={(e) => time_change(e)} />
{/if}
